import { useState, useEffect, useRef } from "react";
import Select from 'react-select';
import { get, post } from "../../utils/ApiRequest";
import { MdErrorOutline } from "react-icons/md";
import validator from 'validator';
import { PURPLE } from "../../utils/designUtils";

const selectStyles = {
    control: (base, state) => ({
        ...base, minHeight: 42, borderRadius: 10,
        border: `1.5px solid ${state.isFocused ? PURPLE : '#e5e0f7'}`,
        boxShadow: 'none', background: '#faf9fe',
        '&:hover': { borderColor: PURPLE },
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? PURPLE : state.isFocused ? '#f0eeff' : 'white',
        color: state.isSelected ? 'white' : '#1f1d2e',
        cursor: 'pointer',
    }),
    singleValue: (base) => ({ ...base, color: '#1f1d2e' }),
    placeholder: (base) => ({ ...base, color: '#aaa9b7', fontSize: 14 }),
    input: (base) => ({ ...base, color: '#1f1d2e', fontSize: 14 }),
};

export default function CreateUser() {
    const [userInput, setUserInput] = useState({
        organizationCode: '',
        userName: '',
        password1: '',
        password2: '',
        userEmail: '',
        eipCertificated: false,
    });

    const [optionList, setOptionList] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    const password = useRef(null);
    const confirmPassword = useRef(null);
    const userNm = useRef(null);
    const userEmail = useRef(null);

    const [orgCodeErr, setOrgCodeErr] = useState("");
    const [confirmPasswordErr, setConfirmPasswordErr] = useState("");
    const [passwordErr, setPasswordErr] = useState("");
    const [userNmErr, setUserNmErr] = useState("");
    const [userEmailErr, setUserEmailErr] = useState("");

    const searchOrganizations = async (searchWord) => {
        if (!searchWord || searchWord.length < 1) { setOptionList([]); setIsSearching(false); return; }
        try {
            setIsSearching(true);
            const response = await get(`/org/find/organization/${encodeURIComponent(searchWord)}`);
            if (response.ok) {
                const data = await response.json();
                setOptionList(data.map(org => ({
                    value: org.organizationCode,
                    label: org.organizationName,
                    organizationCode: org.organizationCode,
                    organizationName: org.organizationName,
                })));
            } else {
                setOptionList([]);
            }
        } catch {
            setOptionList([]);
        } finally {
            setIsSearching(false);
        }
    };

    function handleInputChange(event) {
        const { name, value, type, checked } = event.target;
        if (name === 'userEmail' && validator.isEmail(value) === false) {
            setUserEmailErr("유효하지 않은 이메일 형식입니다.");
            userEmail.current.focus();
            return;
        }
        setUserInput(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }

    const handleOrganizationChange = (selectedOption) => {
        setSelectedOrganization(selectedOption);
        setUserInput(prev => ({ ...prev, organizationCode: selectedOption ? selectedOption.value : '' }));
        if (selectedOption) setOrgCodeErr("");
    };

    const searchTimerRef = useRef(null);
    const handleInputValueChange = (inputValue) => {
        setInputValue(inputValue);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        if (inputValue.length < 1) { setMenuIsOpen(false); setOptionList([]); return; }
        searchTimerRef.current = setTimeout(async () => {
            await searchOrganizations(inputValue);
            setMenuIsOpen(true);
        }, 300);
    };

    useEffect(() => {
        return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
    }, []);

    const filterOption = () => true;

    const CustomOption = ({ innerRef, innerProps, data, isSelected, isFocused }) => (
        <div ref={innerRef} {...innerProps} style={{
            padding: '8px 12px',
            backgroundColor: isSelected ? PURPLE : isFocused ? '#f0eeff' : 'white',
            color: isSelected ? 'white' : '#1f1d2e',
            cursor: 'pointer',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
            <span style={{ fontWeight: 400, fontSize: 14 }}>{data.organizationName}</span>
            <span style={{ color: isSelected ? 'rgba(255,255,255,0.8)' : '#6c757d', fontSize: 12.5 }}>
                {data.organizationCode}
            </span>
        </div>
    );

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!userInput.organizationCode) { setOrgCodeErr("소속을 선택해 주세요"); return; }
        else setOrgCodeErr("");
        if (!userInput.userEmail) { setUserEmailErr("이메일을 입력해 주세요"); userEmail.current.focus(); return; }
        else {
            const response = await post('/user/validUser', userInput.userEmail);
            const isDup = await response.text();
            if (isDup === "1") { setUserEmailErr("이미 사용중인 이메일입니다."); userEmail.current.focus(); return; }
            else setUserEmailErr("");
        }
        if (!userInput.password1) { setPasswordErr("사용하실 비밀번호를 입력해 주세요"); password.current.focus(); return; }
        else {
            setPasswordErr("");
            if (userInput.password1 !== confirmPassword.current.value) {
                setConfirmPasswordErr("비밀번호가 일치하지 않습니다.");
                confirmPassword.current.focus(); return;
            } else setConfirmPasswordErr("");
        }
        if (!userInput.userName) { setUserNmErr("이름을 입력해 주세요"); userNm.current.focus(); return; }
        else setUserNmErr("");
        try {
            const response = await post('/user/createUser', userInput);
            if (response.ok) {
                const result = await response.text();
                alert(result);
                window.location.href = "/";
            } else {
                alert('요청 처리 중 오류가 발생했습니다.');
            }
        } catch (error) {
            alert(`에러 발생: ${error.message}`);
        }
    };

    const inputS = {
        width: '100%', height: 42, borderRadius: 10, border: '1.5px solid #e5e0f7',
        background: '#faf9fe', padding: '0 14px', fontSize: 14, color: '#1f1d2e',
        outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', boxShadow: 'none',
    };
    const labelS = { display: 'block', fontSize: 13.5, fontWeight: 600, color: '#3a3849', marginBottom: 6 };
    const fieldS = { marginBottom: 18 };
    const errS = { display: 'flex', alignItems: 'center', gap: 6, color: '#ef4444', fontSize: 12.5, marginTop: 5 };

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f5f4fa', padding: '32px 16px' }}>
            <div style={{
                background: '#fff', borderRadius: 16,
                boxShadow: '0 4px 24px rgba(124,107,217,0.10)', border: '1px solid rgba(124,107,217,0.12)',
                padding: '40px', width: '100%', maxWidth: 520,
            }}>
                <h2 style={{ textAlign: 'center', fontWeight: 700, marginBottom: 28, color: '#1f1d2e', fontSize: 20, marginTop: 0 }}>
                    회원 가입
                </h2>

                <form onSubmit={handleSubmit} style={{ maxWidth: 'none', margin: 0, padding: 0, border: 'none', borderRadius: 0, background: 'none' }}>
                    <div style={fieldS}>
                        <label style={labelS}>소속</label>
                        <Select
                            options={optionList}
                            value={selectedOrganization}
                            onChange={handleOrganizationChange}
                            onInputChange={handleInputValueChange}
                            inputValue={inputValue}
                            filterOption={filterOption}
                            placeholder="조직명 또는 조직코드 검색해주세요."
                            noOptionsMessage={() => {
                                if (isSearching) return "검색 중...";
                                if (inputValue.length < 1) return "조직명 또는 조직코드를 입력해주세요.";
                                return "검색 결과가 없습니다.";
                            }}
                            isClearable
                            isSearchable
                            menuIsOpen={menuIsOpen}
                            onMenuOpen={() => {
                                if (inputValue.length >= 1 && optionList.length > 0) setMenuIsOpen(true);
                            }}
                            onMenuClose={() => setMenuIsOpen(false)}
                            styles={selectStyles}
                            components={{ Option: CustomOption, DropdownIndicator: () => null }}
                        />
                        {orgCodeErr && <div style={errS}><MdErrorOutline size={16}/>{orgCodeErr}</div>}
                    </div>

                    <div style={fieldS}>
                        <label style={labelS}>이메일</label>
                        <input name="userEmail" type="text" ref={userEmail} onChange={handleInputChange} style={inputS}
                            onFocus={e => e.target.style.borderColor = PURPLE}
                            onBlur={e => e.target.style.borderColor = '#e5e0f7'}/>
                        {userEmailErr && <div style={errS}><MdErrorOutline size={16}/>{userEmailErr}</div>}
                    </div>

                    <div style={fieldS}>
                        <label style={labelS}>비밀번호</label>
                        <input name="password1" type="password" ref={password} onChange={handleInputChange} style={inputS}
                            onFocus={e => e.target.style.borderColor = PURPLE}
                            onBlur={e => e.target.style.borderColor = '#e5e0f7'}/>
                        {passwordErr && <div style={errS}><MdErrorOutline size={16}/>{passwordErr}</div>}
                    </div>

                    <div style={fieldS}>
                        <label style={labelS}>비밀번호 확인</label>
                        <input name="password2" type="password" ref={confirmPassword} onChange={handleInputChange} style={inputS}
                            onFocus={e => e.target.style.borderColor = PURPLE}
                            onBlur={e => e.target.style.borderColor = '#e5e0f7'}/>
                        {confirmPasswordErr && <div style={errS}><MdErrorOutline size={16}/>{confirmPasswordErr}</div>}
                    </div>

                    <div style={fieldS}>
                        <label style={labelS}>성명</label>
                        <input name="userName" type="text" ref={userNm} onChange={handleInputChange} style={inputS}
                            onFocus={e => e.target.style.borderColor = PURPLE}
                            onBlur={e => e.target.style.borderColor = '#e5e0f7'}/>
                        {userNmErr && <div style={errS}><MdErrorOutline size={16}/>{userNmErr}</div>}
                    </div>

                    <button type="submit" style={{
                        width: '100%', height: 46, borderRadius: 12, border: 'none',
                        background: `linear-gradient(135deg, ${PURPLE}, #9b8de8)`,
                        color: 'white', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                    }}>확인</button>
                </form>
            </div>
        </div>
    );
}
